import { ArrowRight } from "lucide-react";
import { scrollToId } from "../constants";
import { Reveal, FrequencyWave } from "./motion";

const outcomes = [
  "Greater presence",
  "Improved focus",
  "Stronger vocal awareness",
  "Intentional confidence",
  "Better preparation before calls",
  "Support between high-pressure interactions",
  "Space to reset after rejection",
  "Awareness of the state brought into conversations",
];

const sequence = ["PAUSE", "RESET", "RE-ENTER"];

const applications = [
  "Leadership Teams",
  "Customer Success",
  "Customer Service",
  "Employee Wellness Programs",
  "Presenters and Speakers",
  "High-Pressure Teams",
  "Creative Teams",
  "Meeting Preparation",
  "Focus Breaks",
  "Transition Between Difficult Conversations",
  "Voice and Presence Development",
  "Anywhere the Next Moment Matters",
];

const intersection = ["Workplace Wellness", "Human Performance", "Sound", "Voice", "Presence"];

const offerings = [
  "Guided team reset sessions",
  "Short custom audio resets",
  "Virtual or in-person workshops",
  "Voice and presence experiences",
  "Sales-team reset practices",
  "Customized workplace wellness sessions",
  "Small pilot programs",
  "Consulting around integrating short reset practices into the workday",
];

const states = ["Calm", "Focus", "Confidence", "Energy", "Presence", "Connection", "Readiness"];

const TonalIntentions = () => (
  <section id="tonal-intentions" data-testid="tonal-intentions-section" className="relative">
    <div className="relative mx-auto max-w-7xl overflow-hidden px-6 py-32 lg:px-12 lg:py-40">
      <div className="pointer-events-none absolute right-0 top-24 h-[30rem] w-[30rem] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.12)_0%,transparent_65%)]" />

      <Reveal>
        <p className="mb-6 text-[0.65rem] font-light uppercase tracking-[0.45em] text-[#8a6d1f]">
          For Business &amp; Workplace
        </p>
        <h2
          data-testid="tonal-headline"
          className="font-serif-display max-w-3xl text-4xl font-light leading-[1.1] tracking-tight text-[#16130e] sm:text-5xl"
        >
          Workplace Wellness for the <span className="text-gold-gradient">Moments That Matter</span>
        </h2>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-10 max-w-2xl space-y-5 text-sm font-extralight leading-relaxed text-black/60 md:text-base">
          <p>
            The SEAL Collective brings sound, voice, presence, and intentional state regulation into the
            workplace through short, practical experiences designed for real moments of pressure, transition,
            and performance.
          </p>
          <p>
            Organizations invest heavily in training, systems, technology, and strategy. But the human being
            still enters every conversation carrying a state.
          </p>
          <p>
            Stress, rejection, distraction, pressure, fatigue, or frustration can influence voice, presence,
            communication, listening, and connection.
          </p>
          <p className="text-black/80">
            The SEAL Collective creates space between moments so people can intentionally reset before moving
            into what comes next.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-16 border-l border-[#b8912e]/50 pl-8">
          <p
            data-testid="tonal-keyline"
            className="font-serif-display max-w-2xl text-3xl font-light leading-snug text-gold-gradient md:text-4xl"
          >
            DON'T CARRY THE LAST MOMENT INTO THE NEXT ONE.
          </p>
        </div>
      </Reveal>
    </div>

    <div className="theme-dark relative overflow-hidden bg-[#0a0908] py-28 lg:py-36">
      <FrequencyWave tone="light" className="absolute top-6 left-0 h-24 w-full" opacity={0.3} />
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-12">
        <Reveal>
          <h3
            data-testid="tonal-sales-headline"
            className="font-serif-display max-w-3xl text-3xl font-light leading-tight text-white sm:text-4xl"
          >
            Built for <span className="text-gold-gradient-light">High-Impact Sales Teams</span>
          </h3>
          <div className="mt-8 max-w-2xl space-y-5 text-sm font-extralight leading-relaxed text-white/55 md:text-base">
            <p>Sales professionals move quickly from one conversation into another.</p>
            <p>
              A difficult call, rejection, missed opportunity, pressure around quota, or mental fatigue can
              easily carry into the next interaction.
            </p>
            <p className="text-white/80">
              Short guided resets can create a deliberate transition before the next conversation begins.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <p className="mt-12 text-[0.6rem] font-light uppercase tracking-[0.35em] text-white/40">
            What teams may experience
          </p>
          <div data-testid="tonal-outcomes" className="mt-5 grid max-w-4xl grid-cols-1 gap-x-10 gap-y-3 sm:grid-cols-2">
            {outcomes.map((o) => (
              <p key={o} className="flex items-baseline gap-3 text-sm font-extralight text-white/70">
                <span className="h-1 w-1 shrink-0 translate-y-[-3px] rounded-full bg-[#d4af37]" />
                {o}
              </p>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.16}>
          <div
            data-testid="tonal-sequence"
            className="mt-16 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center"
          >
            {sequence.map((step, i) => (
              <div key={step} className="flex items-center gap-3">
                <div className="flex-1 border hairline-light bg-white/[0.03] px-8 py-5 text-center sm:flex-none">
                  <span className="text-[0.65rem] font-light uppercase tracking-[0.35em] text-white/75">
                    {step}
                  </span>
                </div>
                {i < sequence.length - 1 && (
                  <ArrowRight
                    size={14}
                    strokeWidth={1.5}
                    className="mx-auto rotate-90 text-[#d4af37]/70 sm:rotate-0"
                  />
                )}
              </div>
            ))}
          </div>
          <p className="font-serif-display mt-8 text-xl font-light italic text-[#f3e5ab] md:text-2xl">
            Train the state behind the skill.
          </p>
        </Reveal>
      </div>
    </div>

    <div className="mx-auto max-w-7xl px-6 py-32 lg:px-12">
      <Reveal>
        <h3
          data-testid="tonal-broader-headline"
          className="font-serif-display max-w-3xl text-3xl font-light leading-tight text-[#16130e] sm:text-4xl"
        >
          Beyond <span className="text-gold-gradient">Sales</span>
        </h3>
      </Reveal>

      <div
        className="mt-12 grid grid-cols-1 gap-px bg-black/10 sm:grid-cols-2 lg:grid-cols-4"
        data-testid="tonal-applications"
      >
        {applications.map((a, i) => (
          <div key={a} className="bg-[#faf8f4] p-6">
            <Reveal delay={i * 0.03}>
              <p className="text-sm font-light text-black/70">{a}</p>
            </Reveal>
          </div>
        ))}
      </div>

      <Reveal delay={0.08}>
        <div className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-2">
          {intersection.map((w, i) => (
            <span key={w} className="flex items-center gap-3">
              <span className="font-serif-display text-lg font-light italic text-[#8a6d1f]">{w}</span>
              {i < intersection.length - 1 && <span className="text-black/25">+</span>}
            </span>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.12}>
        <div className="mt-24 border-t hairline pt-16">
          <p className="mb-6 text-[0.65rem] font-light uppercase tracking-[0.45em] text-[#8a6d1f]">
            Available Now
          </p>
          <p className="max-w-2xl text-sm font-extralight leading-relaxed text-black/60 md:text-base">
            Organizations can currently explore customized experiences including:
          </p>
          <ul
            data-testid="tonal-delivery"
            className="mt-8 grid max-w-3xl grid-cols-1 gap-x-12 gap-y-4 sm:grid-cols-2"
          >
            {offerings.map((d) => (
              <li key={d} className="flex items-baseline gap-3 text-sm font-light text-black/70">
                <span className="h-1 w-1 shrink-0 translate-y-[-3px] rounded-full bg-[#b8912e]" />
                {d}
              </li>
            ))}
          </ul>
          <div className="mt-12 flex flex-col gap-4 sm:flex-row">
            <button data-testid="tonal-primary-cta" onClick={() => scrollToId("contact")} className="btn-solid">
              Explore a Team Experience
            </button>
            <button
              data-testid="tonal-secondary-cta"
              onClick={() => scrollToId("contact")}
              className="btn-outline"
            >
              Start a Conversation
            </button>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div
          data-testid="tonal-future-panel"
          className="mt-24 border border-[#b8912e]/30 bg-white/60 p-10 md:p-14"
        >
          <p className="mb-6 text-[0.65rem] font-light uppercase tracking-[0.45em] text-[#8a6d1f]">
            Future Direction
          </p>
          <h4
            data-testid="tonal-future-headline"
            className="font-serif-display text-2xl font-light text-[#16130e] md:text-3xl"
          >
            Tonal Intentions™ — In Development
          </h4>
          <div className="mt-6 max-w-2xl space-y-4 text-sm font-extralight leading-relaxed text-black/60 md:text-base">
            <p>
              Tonal Intentions™ is an emerging sound-based experience exploring intentional state transition
              through tone, voice, rhythm, resonance, breath, and frequency-informed audio.
            </p>
            <p>
              The concept is simple:{" "}
              <span className="font-serif-display text-lg italic text-[#8a6d1f]">How are you arriving?</span>{" "}
              <span className="font-serif-display text-lg italic text-[#8a6d1f]">
                How do you intend to arrive?
              </span>
            </p>
            <p>
              Future Tonal Intentions experiences are being developed around short tonal sequences designed to
              support the transition between those two states.
            </p>
          </div>
          <p className="mt-10 text-[0.6rem] font-light uppercase tracking-[0.35em] text-black/40">
            Possible intended states
          </p>
          <div data-testid="tonal-states" className="mt-4 flex flex-wrap gap-2">
            {states.map((s) => (
              <span
                key={s}
                className="font-serif-display border border-[#b8912e]/40 bg-[#b8912e]/10 px-5 py-2 text-base font-light italic text-[#8a6d1f]"
              >
                {s}
              </span>
            ))}
          </div>
          <p className="mt-10 max-w-2xl text-sm font-extralight leading-relaxed text-black/60">
            For organizations interested in exploring the methodology today, customized guided resets and team
            experiences are available now.
          </p>
          <button
            data-testid="tonal-explore-work-button"
            onClick={() => scrollToId("contact")}
            className="btn-outline mt-8"
          >
            Explore the Work
          </button>
        </div>
      </Reveal>
    </div>
  </section>
);

export default TonalIntentions;
